import React, { useEffect, useState } from 'react';
import api, { digilockerAPI, employeeAPI, documentAPI } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import ErrorMessage from '../components/Common/ErrorMessage';
import SuccessMessage from '../components/Common/SuccessMessage';

const DigiLockerPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [documents, setDocuments] = useState([]);
  const [selectedDocId, setSelectedDocId] = useState('');
  const [xmlPreview, setXmlPreview] = useState('');
  const [apiKey, setApiKey] = useState('9c1bfb8f7e9f8afd3ff4c6039894fe8aab46472a14469eafd4c54005d4d97cd4');
  const [identifiersOnly, setIdentifiersOnly] = useState(true);
  const [testData, setTestData] = useState({
    xmlContent: `<?xml version="1.0" encoding="UTF-8"?>
<PullURIRequest xmlns="http://tempuri.org/">
  <TxnId>TXN123456789</TxnId>
  <DocType>IDCRD</DocType>
  <Udf1>EMP2025-001</Udf1>
  <FullName>John Doe</FullName>
  <Dob>1990-01-01</Dob>
</PullURIRequest>`,
    hmac: ''
  });
  const [pullResponse, setPullResponse] = useState('');

  // Resolve absolute URL for static files
  const apiBase = (api?.defaults?.baseURL || '').replace(/\/?api\/?$/, '');
  const resolveUrl = (p) => {
    if (!p) return null;
    let s = String(p).trim();
    if (!s) return null;
    if (/^(https?:|data:)/i.test(s)) return s;
    s = s.replace(/\\/g, '/');
    if (s.startsWith('./')) s = s.slice(2);
    if (s.startsWith('/')) s = s.slice(1);
    const idx = s.toLowerCase().indexOf('files/');
    if (idx >= 0) s = s.slice(idx);
    if (!/^files\//i.test(s)) s = `files/${s}`;
    return `${apiBase}/${s}`;
  };

  // Browser SHA-256 helper (hex)
  const sha256Hex = async (text) => {
    const enc = new TextEncoder();
    const data = enc.encode(text);
    const hash = await crypto.subtle.digest('SHA-256', data);
    const bytes = Array.from(new Uint8Array(hash));
    return bytes.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Generate current IST timestamp as YYYY-MM-DDTHH:mm:ss+05:30 (explicit IST offset)
  const getISTTimestamp = () => {
    const now = new Date();
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
    const ist = new Date(utcMs + 5.5 * 60 * 60000); // UTC + 05:30
    const pad = (v) => String(v).padStart(2, '0');
    return `${ist.getFullYear()}-${pad(ist.getMonth() + 1)}-${pad(ist.getDate())}T${pad(ist.getHours())}:${pad(ist.getMinutes())}:${pad(ist.getSeconds())}+05:30`;
  };

  // Load employees on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await employeeAPI.getAll(1, 500);
        setEmployees(res.data.data || []);
      } catch (e) {
        console.error('Failed to load employees', e);
      }
    })();
  }, []);

  // When employee changes, load documents
  useEffect(() => {
    if (!selectedEmployeeId) { setDocuments([]); setSelectedDocId(''); setXmlPreview(''); return; }
    (async () => {
      try {
        const res = await documentAPI.getByEmployee(selectedEmployeeId);
        setDocuments(res.data.data || []);
      } catch (e) {
        console.error('Failed to load documents', e);
      }
    })();
  }, [selectedEmployeeId]);

  // Load XML preview when document selected, and auto-compose PullURI XML + HMAC
  useEffect(() => {
    if (!selectedDocId) { setXmlPreview(''); return; }
    const doc = documents.find(d => d.doc_id === selectedDocId);
    if (!doc) { setXmlPreview(''); return; }

    // Compose PullURI XML from current selection
    const emp = employees.find(e => e.employee_id === selectedEmployeeId);
    if (emp && doc) {
      (async () => {
        const ts = getISTTimestamp();
        const digilockerId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
          const r = Math.random() * 16 | 0; const v = c === 'x' ? r : (r & 0x3 | 0x8); return v.toString(16);
        });
        const keyhash = apiKey ? await sha256Hex(apiKey + ts) : 'test-keyhash';
        const nameDob = identifiersOnly ? '' : `\n    <FullName>${emp.full_name}</FullName>\n    <DOB>${emp.dob || ''}</DOB>`;
        const requestXml = `<?xml version="1.0" encoding="UTF-8"?>\n<PullURIRequest xmlns="http://tempuri.org" ver="3.0" ts="${ts}" txn="123456" orgId="maharashtra.gov.in" keyhash="${keyhash}" format="both">\n  <DocDetails>\n    <DocType>${doc.doc_type}</DocType>\n    <DigiLockerId>${digilockerId}</DigiLockerId>\n    <UID></UID>${nameDob}\n    <UDF1>${emp.employee_id}</UDF1>\n    <UDF2>${emp.department || ''}</UDF2>\n  </DocDetails>\n</PullURIRequest>`;
        setTestData(prev => ({ ...prev, xmlContent: requestXml }));
        try {
          const resp = await digilockerAPI.testHmac({ body: requestXml });
          setTestData(prev => ({ ...prev, hmac: resp.data.hmac }));
        } catch (e) {
          console.error('Auto HMAC generation failed', e);
        }
      })();
    }

    // Load document XML metadata preview if available
    if (doc?.xml_metadata_path) {
      const url = resolveUrl(doc.xml_metadata_path);
      if (!url) return;
      (async () => {
        try {
          const resp = await fetch(url);
          const text = await resp.text();
          setXmlPreview(text);
        } catch (e) {
          console.error('Failed to fetch XML', e);
          setXmlPreview('');
        }
      })();
    } else {
      setXmlPreview('');
    }
  }, [selectedDocId, documents, employees, selectedEmployeeId, apiKey]);

  const handleHealthCheck = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await digilockerAPI.healthCheck();
      setSuccess('Health check successful! Server is running.');
    } catch (err) {
      setError('Health check failed. Please check if the backend server is running.');
      console.error('Health check error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTestHmac = async () => {
    try {
      setLoading(true);
      setError(null);
      const testBody = 'This is a test body for HMAC generation';
      const response = await digilockerAPI.testHmac({ body: testBody });
      setSuccess('HMAC test successful!');
      setTestData(prev => ({
        ...prev,
        hmac: response.data.hmac || 'Generated HMAC'
      }));
    } catch (err) {
      setError('HMAC test failed: ' + (err.response?.data?.error || err.message));
      console.error('HMAC test error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePullUriRequest = async () => {
    if (!testData.xmlContent.trim()) {
      setError('Please enter XML content for the Pull URI request.');
      return;
    }

    if (!testData.hmac.trim()) {
      setError('Please generate HMAC first using the Generate HMAC button.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('Sending Pull URI Request:');
      console.log('XML Content:', testData.xmlContent);
      console.log('HMAC:', testData.hmac);
      console.log('Content-Type: application/xml');
      
      // Send Pull URI request with XML content and HMAC
      const response = await digilockerAPI.pullUriRequest(testData.xmlContent, testData.hmac);
      setSuccess('Pull URI request sent successfully!');
      setPullResponse(typeof response.data === 'string' ? response.data : JSON.stringify(response.data, null, 2));
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Pull URI request failed.';
      setError('Pull URI request failed: ' + errorMessage);
      console.error('Pull URI request error:', err);
      console.error('Error response:', err.response?.data);
      if (typeof err.response?.data === 'string') setPullResponse(err.response.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="digilocker-page">
      <div className="page-header">
        <h2>DigiLocker Integration</h2>
        <p>Test and manage DigiLocker API endpoints</p>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError(null)} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess(null)} />}

      <div className="digilocker-sections">
        <div className="section">
          <h3>Test With Existing Employee Document</h3>
          <div className="form-row" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div className="form-group" style={{ minWidth: 240 }}>
              <label>Employee</label>
              <select className="form-control" value={selectedEmployeeId} onChange={(e) => setSelectedEmployeeId(e.target.value)}>
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>{emp.full_name} ({emp.employee_id})</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ minWidth: 220 }}>
              <label>Document</label>
              <select className="form-control" value={selectedDocId} onChange={(e) => setSelectedDocId(e.target.value)} disabled={!selectedEmployeeId}>
                <option value="">Select Document</option>
                {documents.map(d => (
                  <option key={d.doc_id} value={d.doc_id}>{d.doc_id} ({d.doc_type})</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ alignSelf: 'end' }}>
              <button
                className="btn btn-secondary"
                disabled={!selectedDocId}
                onClick={async () => {
                  // Fill request XML from selection
                  const emp = employees.find(e => e.employee_id === selectedEmployeeId);
                  const doc = documents.find(d => d.doc_id === selectedDocId);
                  if (!emp || !doc) return;
                  const ts = getISTTimestamp();
                  const digilockerId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => { const r = Math.random() * 16 | 0; const v = c === 'x' ? r : (r & 0x3 | 0x8); return v.toString(16); });
                  const keyhash = apiKey ? await sha256Hex(apiKey + ts) : 'test-keyhash';
                  const nameDob = identifiersOnly ? '' : `\n    <FullName>${emp.full_name}</FullName>\n    <DOB>${emp.dob || ''}</DOB>`;
                  const requestXml = `<?xml version="1.0" encoding="UTF-8"?>\n<PullURIRequest xmlns="http://tempuri.org" ver="3.0" ts="${ts}" txn="123456" orgId="maharashtra.gov.in" keyhash="${keyhash}" format="both">\n  <DocDetails>\n    <DocType>${doc.doc_type}</DocType>\n    <DigiLockerId>${digilockerId}</DigiLockerId>\n    <UID></UID>${nameDob}\n    <UDF1>${emp.employee_id}</UDF1>\n    <UDF2>${emp.department || ''}</UDF2>\n  </DocDetails>\n</PullURIRequest>`;
                  setTestData(prev => ({ ...prev, xmlContent: requestXml }));
                  setSuccess('Pull URI XML composed from selection. Generate HMAC and send.');
                }}
              >Compose Pull URI XML</button>
            </div>
            <div className="form-group" style={{ minWidth: 220 }}>
              <label>
                <input type="checkbox" checked={identifiersOnly} onChange={(e) => setIdentifiersOnly(e.target.checked)} />
                &nbsp;Identifiers-only (omit FullName/DOB)
              </label>
            </div>
          </div>

          {xmlPreview && (
            <div className="result-box" style={{ marginTop: 8 }}>
              <label>Document XML Metadata (preview)</label>
              <code style={{ whiteSpace: 'pre-wrap' }}>{xmlPreview}</code>
            </div>
          )}
        </div>

        <div className="section">
          <h3>Pull URI Request</h3>
          <p>Send a Pull URI request to DigiLocker.</p>
          
            <div className="form-group">
            <label htmlFor="xmlContent">XML Content:</label>
            <textarea
              id="xmlContent"
              value={testData.xmlContent}
              onChange={(e) => setTestData(prev => ({ ...prev, xmlContent: e.target.value }))}
              className="form-control"
              rows="6"
              placeholder="Enter XML content for Pull URI request..."
            />
          </div>

            <div className="form-group">
              <label htmlFor="apiKey">API Key (used to compute keyhash = SHA256(API_KEY + ts))</label>
              <input
                type="text"
                id="apiKey"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="form-control"
                placeholder="Enter backend API key to compute keyhash"
              />
            </div>

          <div className="form-group">
            <label htmlFor="hmac">HMAC:</label>
            <div className="d-flex gap-2">
              <input
                type="text"
                id="hmac"
                value={testData.hmac}
                onChange={(e) => setTestData(prev => ({ ...prev, hmac: e.target.value }))}
                className="form-control"
                placeholder="HMAC will be generated automatically"
                readOnly
              />
              <button 
                className="btn btn-info" 
                onClick={async () => {
                  try {
                    setLoading(true);
                    setError(null);
                    
                    // Generate HMAC for the exact XML content that will be sent
                    const response = await digilockerAPI.testHmac({ body: testData.xmlContent });
                    setTestData(prev => ({ ...prev, hmac: response.data.hmac }));
                    setSuccess('HMAC generated for XML content!');
                    
                    console.log('Generated HMAC for XML:', testData.xmlContent);
                    console.log('Generated HMAC:', response.data.hmac);
                  } catch (err) {
                    setError('Failed to generate HMAC: ' + (err.response?.data?.error || err.message));
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading || !testData.xmlContent.trim()}
              >
                Generate HMAC
              </button>
            </div>
          </div>

          <button 
            className="btn btn-warning" 
            onClick={handlePullUriRequest}
            disabled={loading || !testData.xmlContent.trim() || !testData.hmac.trim()}
          >
            {loading ? 'Sending...' : 'Send Pull URI Request'}
          </button>
          {pullResponse && (
            <div className="result-box" style={{ marginTop: 16 }}>
              <label>Response</label>
              <code style={{ whiteSpace: 'pre-wrap' }}>{pullResponse}</code>
              <div style={{ marginTop: 8 }}>
                <button className="btn btn-outline" onClick={() => navigator.clipboard.writeText(pullResponse)}>Copy</button>
                <button className="btn btn-outline" style={{ marginLeft: 8 }} onClick={() => setPullResponse('')}>Clear</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="info-section">
        <h3>API Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <strong>Base URL:</strong>
            <code>https://digilocker.srpfpunegroup1.in/api</code>
          </div>
          <div className="info-item">
            <strong>Pull URI Endpoint:</strong>
            <code>POST /api/pullUriRequest</code>
          </div>
          <div className="info-item">
            <strong>Health Check:</strong>
            <code>GET /api/health</code>
          </div>
          <div className="info-item">
            <strong>HMAC Test:</strong>
            <code>POST /api/test-hmac</code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigiLockerPage;
